import React, { useEffect, useState } from "react";
import api, { extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Upload, Trash2, Loader2, Image as ImageIcon } from "lucide-react";

export const ImpostazioniAccessi = ({ config, onSaved }) => {
  const [gruppiText, setGruppiText] = useState("");
  const [report, setReport] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (config) {
      setGruppiText((config.gruppi || []).join("\n"));
      setReport(config.report || {});
      setHasLogo(!!config.has_logo);
    }
  }, [config]);

  const loadLogo = async () => {
    try {
      const res = await api.get("/accessi/impostazioni/logo", { responseType: "blob" });
      setLogoUrl(URL.createObjectURL(res.data));
    } catch (e) { setLogoUrl(""); }
  };
  useEffect(() => { if (hasLogo) loadLogo(); }, [hasLogo]);

  const save = async () => {
    setSaving(true);
    const gruppi = gruppiText.split("\n").map((g) => g.trim()).filter(Boolean);
    try {
      await api.put("/accessi/impostazioni", { gruppi, report });
      toast.success("Impostazioni salvate");
      onSaved && onSaved();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/accessi/impostazioni/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo caricato"); setHasLogo(true); await loadLogo(); onSaved && onSaved();
    } catch (e) { toast.error(extractError(e)); } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    try { await api.delete("/accessi/impostazioni/logo"); toast.success("Logo rimosso"); setHasLogo(false); setLogoUrl(""); onSaved && onSaved(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const upd = (patch) => setReport((r) => ({ ...r, ...patch }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 min-w-0">
        <div className="font-display font-semibold text-slate-900 mb-1">Scuole / Gruppi</div>
        <p className="text-xs text-muted-foreground mb-2">Elenco pre-codificato selezionabile negli eventi e nelle affiliazioni (uno per riga).</p>
        <Textarea value={gruppiText} onChange={(e) => setGruppiText(e.target.value)} rows={10} data-testid="imp-gruppi" />
      </Card>

      <Card className="p-5 min-w-0">
        <div className="font-display font-semibold text-slate-900 mb-3">Intestazione report</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Denominazione</Label><Input value={report.denominazione || ""} onChange={(e) => upd({ denominazione: e.target.value })} data-testid="imp-denominazione" /></div>
          <div>
            <Label>Colore primario</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={report.colore_primario || "#1976D2"} onChange={(e) => upd({ colore_primario: e.target.value })} className="h-10 w-12 rounded border cursor-pointer" data-testid="imp-colore-primario" />
              <Input value={report.colore_primario || "#1976D2"} onChange={(e) => upd({ colore_primario: e.target.value })} className="flex-1" />
            </div>
          </div>
          <div>
            <Label>Colore secondario</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={report.colore_secondario || "#F57C00"} onChange={(e) => upd({ colore_secondario: e.target.value })} className="h-10 w-12 rounded border cursor-pointer" data-testid="imp-colore-secondario" />
              <Input value={report.colore_secondario || "#F57C00"} onChange={(e) => upd({ colore_secondario: e.target.value })} className="flex-1" />
            </div>
          </div>
          <div>
            <Label>Modello grafico</Label>
            <Select value={report.template || "istituzionale"} onValueChange={(v) => upd({ template: v })}>
              <SelectTrigger data-testid="imp-template"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="istituzionale">Istituzionale</SelectItem>
                <SelectItem value="moderno">Moderno</SelectItem>
                <SelectItem value="compatto">Compatto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Orientamento</Label>
            <Select value={report.orientamento || "verticale"} onValueChange={(v) => upd({ orientamento: v })}>
              <SelectTrigger data-testid="imp-orientamento"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="verticale">Verticale (A4)</SelectItem>
                <SelectItem value="orizzontale">Orizzontale (A4)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Testo piè di pagina</Label><Input value={report.footer_text || ""} onChange={(e) => upd({ footer_text: e.target.value })} data-testid="imp-footer" /></div>
          <div className="sm:col-span-2 flex items-center justify-between rounded-lg border p-3 bg-slate-50">
            <Label htmlFor="imp-pagine" className="cursor-pointer">Mostra numero di pagina</Label>
            <Switch id="imp-pagine" checked={report.mostra_numero_pagine !== false} onCheckedChange={(v) => upd({ mostra_numero_pagine: v })} data-testid="imp-pagine" />
          </div>
        </div>

        <div className="mt-4">
          <Label>Logo intestazione</Label>
          <div className="flex items-center gap-3 mt-1">
            <div className="h-16 w-28 rounded-lg border bg-white flex items-center justify-center overflow-hidden">
              {logoUrl ? <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="imp-logo-preview" /> : <ImageIcon className="h-6 w-6 text-slate-300" />}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex">
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="imp-logo-file" />
                <span className="inline-flex items-center text-sm px-3 py-2 rounded-md border cursor-pointer hover:bg-slate-50">
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Carica logo
                </span>
              </label>
              {hasLogo && <Button variant="ghost" size="sm" onClick={removeLogo} className="text-[color:var(--brand-red-600)]" data-testid="imp-logo-remove"><Trash2 className="h-4 w-4 mr-1" /> Rimuovi</Button>}
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="imp-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva impostazioni
          </Button>
        </div>
      </Card>
    </div>
  );
};
